import styled from 'styled-components';

import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { colors, spacing } from 'ui/theme';
import { button } from 'ui/theme/typography';

type StyledHeaderButtonProps = {
  isActiveSortColumn: boolean;
  isSortable: boolean;
};

export const HeaderButton = styled.button<StyledHeaderButtonProps>`
  ${button}
  align-items: center;
  background: none;
  border: none;
  color: ${(props: StyledHeaderButtonProps) =>
    props.isActiveSortColumn ? colors.purple100 : colors.textPrimary};
  cursor: ${(props: StyledHeaderButtonProps) => (props.isSortable ? 'pointer' : 'default')};
  display: flex;
  outline: none;
  padding: ${spacing.half} 0;
  pointer-events: ${(props: StyledHeaderButtonProps) => (props.isSortable ? 'auto' : 'none')};
  text-align: left;

  span {
    display: inline-block;
    flex: 0 1 auto;
    margin-right: ${spacing.quarter};
  }
  svg {
    flex: 0 0 auto;
  }
`;

type Props = {
  /** Unique id for this column */
  id: string;
  /** True if column is active sort column  */
  isActiveSortColumn: boolean;
  /** True if column is sortable */
  isSortable: boolean;
  /** Label to display in column header */
  label: string;
  /** Function run when sortBy value is changed */
  onSortByChange: (newSortedById: string) => void;
  /** Function run when sort direction is changed */
  onSortDirectionChange: (sortDirection: SORT_DIRECTIONS) => void;
  /** Current sort direction */
  sortDirection: SORT_DIRECTIONS;
};

export const testIds = {
  arrowAsc: 'table-header-button-sort-arrow-asc',
  arrowDesc: 'table-header-button-sort-arrow-desc',
  arrowDouble: 'table-header-button-sort-arrow-double',
  button: 'table-header-button',
};

export const TableHeaderButton = ({
  id,
  isActiveSortColumn = false,
  isSortable = false,
  label,
  onSortByChange,
  onSortDirectionChange,
  sortDirection,
  ...otherProps
}: Props) => {
  const { ASCENDING, DESCENDING } = SORT_DIRECTIONS;
  const isAscending = sortDirection === ASCENDING;
  return (
    <HeaderButton
      data-test={`${testIds.button}-${id}`}
      isActiveSortColumn={isActiveSortColumn}
      isSortable={isSortable}
      key={id}
      onClick={() => {
        if (isSortable) {
          // only respond to clicks if the header is sortable
          if (isActiveSortColumn) {
            onSortDirectionChange(isAscending ? DESCENDING : ASCENDING);
          } else {
            onSortByChange(id);
            onSortDirectionChange(DESCENDING);
          }
        }
      }}
      {...otherProps}
    >
      <span>{label}</span>
      {isSortable && !isActiveSortColumn && (
        <DoubleArrow isColorInherited={true} data-test={testIds.arrowDouble} />
      )}
      {isSortable && isActiveSortColumn && isAscending && (
        <UpArrow isColorInherited={true} data-test={testIds.arrowAsc} />
      )}
      {isSortable && isActiveSortColumn && !isAscending && (
        <DownArrow isColorInherited={true} data-test={testIds.arrowDesc} />
      )}
    </HeaderButton>
  );
};
